'use client'

// React Imports
import { useState } from 'react'

// MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'

// Types
import type { ThemeColor } from '@core/types'
import type { UpcomingPaymentsProps, UpcomingPaymentData } from '@/types/dashboards/resumeTypes'

// Components
import IconifyIcon from '@/components/layout/shared/Icons'

// Hooks
import { useDashboardResumeTranslations, useTranslations } from '@/hooks/useTranslations'

// Configuração dos tipos de pagamento
const paymentConfig = {
  expense: {
    icon: 'tabler:receipt',
    color: 'primary' as ThemeColor,
    bgColor: 'rgba(103, 58, 183, 0.1)'
  },
  financing: {
    icon: 'tabler:credit-card',
    color: 'warning' as ThemeColor,
    bgColor: 'rgba(255, 152, 0, 0.1)'
  },
  employee: {
    icon: 'tabler:users',
    color: 'info' as ThemeColor,
    bgColor: 'rgba(33, 150, 243, 0.1)'
  },
  supplier: {
    icon: 'tabler:truck-delivery',
    color: 'secondary' as ThemeColor,
    bgColor: 'rgba(156, 39, 176, 0.1)'
  }
}

// Componente Skeleton para pagamento
const PaymentSkeleton = () => (
  <ListItem sx={{ py: 2 }}>
    <ListItemAvatar>
      <Skeleton variant="circular" width={40} height={40} />
    </ListItemAvatar>
    <ListItemText
      primary={<Skeleton variant="text" width="70%" height={20} />}
      secondary={<Skeleton variant="text" width="90%" height={16} />}
    />
    <Box sx={{ ml: 2 }}>
      <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 1 }} />
    </Box>
  </ListItem>
)

// Função para formatar valor monetário
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const UpcomingPayments = ({
  data,
  loading = false
}: UpcomingPaymentsProps) => {
  const { t } = useDashboardResumeTranslations()
  const { t: commonT } = useTranslations(['common/common', 'common/expenses', 'common/revenues'])
  const theme = useTheme()
  const [showAll, setShowAll] = useState(false)

    // Função para traduzir categorias usando o sistema de tradução padronizado
  const translateCategory = (category: string | undefined): string => {
    if (!category) return ''

    // Usar traduções padronizadas de despesas
    const categoryTranslation = commonT(`expenses.categories.${category}`)

    if (categoryTranslation && categoryTranslation !== `expenses.categories.${category}`) {
      return categoryTranslation
    }

    // Tentar tradução de receitas
    const revenueTranslation = commonT(`revenues.categories.${category}`)
    
    if (revenueTranslation && revenueTranslation !== `revenues.categories.${category}`) {
      return revenueTranslation
    }

    const subcategoryTranslation = commonT(`expenses.subcategories.${category}`)

    if (subcategoryTranslation && subcategoryTranslation !== `expenses.subcategories.${category}`) {
      return subcategoryTranslation
    }

    // Tentar tradução de subcategorias de receitas
    const revenueSubcategoryTranslation = commonT(`revenues.subcategories.${category}`)

    if (revenueSubcategoryTranslation && revenueSubcategoryTranslation !== `revenues.subcategories.${category}`) {
      return revenueSubcategoryTranslation
    }

    // Fallback para o nome original
    return category
  }

  // Função para traduzir description usando o sistema de tradução
  const translateDescription = (description: string | undefined): string => {
    if (!description) return ''

    // Usar traduções padronizadas de despesas
    const subcategoryTranslation = commonT(`expenses.subcategories.${description}`)

    if (subcategoryTranslation && subcategoryTranslation !== `expenses.subcategories.${description}`) {
      return subcategoryTranslation
    }

    const categoryTranslation = commonT(`expenses.categories.${description}`)

    if (categoryTranslation && categoryTranslation !== `expenses.categories.${description}`) {
      return categoryTranslation
    }

    // Fallback para o nome original
    return description
  }

  // Função para formatar data relativa com traduções
  const formatRelativeDate = (dateString: string): string => {
    const now = new Date()
    const dueDate = new Date(dateString)
    const diffInDays = Math.floor((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (diffInDays < 0) {
      return `${Math.abs(diffInDays)}d ${t('upcomingPayments.overdue')}`
    } else if (diffInDays === 0) {
      return t('upcomingPayments.dueToday')
    } else if (diffInDays === 1) {
      return t('upcomingPayments.dueTomorrow')
    } else if (diffInDays <= 7) {
      return `${t('upcomingPayments.dueInDays')} ${diffInDays} ${t('upcomingPayments.days')}`
    } else {
      return dueDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      })
    }
  }

  const paymentsData = data || []
  const displayedPayments = showAll ? paymentsData : paymentsData.slice(0, 12)

  const getStatusChip = (status: UpcomingPaymentData['status']) => {
    const statusConfig = {
      overdue: { label: commonT('status.overdue'), color: 'error' as ThemeColor },
      due_soon: { label: commonT('status.due_soon'), color: 'warning' as ThemeColor },
      scheduled: { label: commonT('status.scheduled'), color: 'info' as ThemeColor }
    }

    const config = statusConfig[status as keyof typeof statusConfig]

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
        sx={{ minWidth: 70 }}
      />
    )
  }

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 450 }}>
        <CardHeader
          title={<Skeleton variant="text" width={180} height={28} />}
        />
        <CardContent sx={{ pt: 0 }}>
          <List sx={{ p: 0 }}>
            {[...Array(5)].map((_, index) => (
              <PaymentSkeleton key={index} />
            ))}
          </List>
        </CardContent>
      </Card>
    )
  }

  // Se não há dados, mostrar mensagem
  if (!loading && paymentsData.length === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 450 }}>
        <CardHeader
          title={
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {t('upcomingPayments.title')}
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 350 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {t('upcomingPayments.noData') || 'Nenhum pagamento encontrado'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ height: '100%', minHeight: 450 }} data-testid="upcoming-payments">
      <CardHeader
        title={
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {t('upcomingPayments.title')}
          </Typography>
        }
        action={
          paymentsData.length > 12 && (
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAll(!showAll)}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            >
              {showAll ? t('upcomingPayments.showLess') : `${t('upcomingPayments.showAll')} (${paymentsData.length})`}
            </Button>
          )
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <List sx={{ p: 0 }}>
          {displayedPayments.map((payment, index) => {
            const config = paymentConfig[payment.type as keyof typeof paymentConfig]

            return (
              <ListItem
                key={payment.id}
                sx={{
                  py: 1.5,
                  px: 0,
                  ...(index !== displayedPayments.length - 1 && {
                    borderBottom: `1px solid ${theme.palette.divider}`
                  })
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: config.bgColor,
                      color: `var(--mui-palette-${config.color}-main)`,
                      width: 36,
                      height: 36
                    }}
                  >
                    <IconifyIcon icon={config.icon} width={18} height={18} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                          {payment.title}
                        </Typography>
                        {payment.farm && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              bgcolor: 'action.hover',
                              borderRadius: 0.5,
                              border: `1px solid ${theme.palette.divider}`
                            }}
                          >
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                              {payment.farm}
                            </Typography>
                          </Box>
                        )}
                        {payment.contractNumber && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              bgcolor: 'info.lighter',
                              borderRadius: 0.5,
                              border: `1px solid ${theme.palette.info.light}`
                            }}
                          >
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: 'info.main' }}>
                              {t('upcomingPayments.contract')}: {payment.contractNumber}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                      >
                        {formatCurrency(payment.value)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {translateDescription(payment.description)}
                          </Typography>
                          {payment.supplier && (
                            <>
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                •
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {payment.supplier}
                              </Typography>
                            </>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color={payment.status === 'overdue' ? 'error.main' : 'text.disabled'}
                          sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                        >
                          {formatRelativeDate(payment.dueDate)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                          {t(`paymentTypes.${payment.type}`)}
                        </Typography>
                        {payment.category && (
                          <>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              •
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {translateCategory(payment.category)}
                            </Typography>
                          </>
                        )}
                        {payment.invoiceNumber && (
                          <>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              •
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {t('upcomingPayments.invoice')}: {payment.invoiceNumber}
                            </Typography>
                          </>
                        )}
                        {payment.bank && payment.account && (
                          <>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              •
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {payment.bank} - {payment.account}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  }
                  slotProps={{
                    secondary: {
                      component: 'div'
                    }
                  }}
                />
                <Box sx={{ ml: 1 }}>
                  {getStatusChip(payment.status)}
                </Box>
              </ListItem>
            )
          })}
        </List>
      </CardContent>
    </Card>
  )
}

export default UpcomingPayments
